<template>
  <article
    class="article-card-small"
    :class="articleCardSmallClass"
  >
    <TestPixel
      v-for="(testPixel, index) of article.test_pixels"
      :key="index"
      :pixel-link="testPixel"
    />
    <a
      class="article-card-small__link"
      :href="removeBurninghutFromUrl(article.url)"
      :target="target"
      :aria-label="`Перейти к статье ${article.title}`"
      @click="sendAnalyticsArticleLink"
    />
    <div class="article-card-small__image-wrap">
      <AdaptivePictureArticleCardSmall
        :picture="article.image"
        :loading="imageLoading"
      />
    </div>
    <div class="article-card-small__body">
      <SecretElems
        v-if="isLoggedInWp"
        class="article-card-small__secret"
        :article="article"
      />
      <div class="article-card-small__description">
        <BHLink
          class="article-card-small__title"
          :href="removeBurninghutFromUrl(article.url)"
          :aria-label="`Перейти к статье ${article.title}`"
          :target="target"
          colorless
          @click="sendAnalyticsArticleLink"
        >
          <span v-html="article.title" />
        </BHLink>
        <p
          v-if="article.excerpt"
          class="article-card-small__description-text"
        >
          {{ article.excerpt }}
        </p>
      </div>
      <div
        v-if="isShowFooter"
        class="article-card-small__footer"
      >
        <CardFooter
          class="article-card-small__footer"
          :article="article"
          :new-tab="newTab"
        />
      </div>
    </div>
  </article>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import CardFooter from '~/components/CardFooter.vue';
  import { getDateText } from '~/utils/date';
  import { removeBurninghutFromUrl } from '~/utils/link';
  import type { TArticle, TImageSize } from '~/types/common';
  import { filterImages } from '~/types/utils/images';
  import type { IMetaArchives } from '~/types/api/archives';
  import BHLink from '~/components/BHLink.vue';
  import SecretElems from '~/components/SecretElems.vue';
  import TestPixel from '~/components/TestPixel.vue';
  import AdaptivePictureArticleCardSmall from '~/components/AdaptivePictureArticleCardSmall.vue';
  import { useUserStore } from '~/features/user/store';
  import { mapState } from 'pinia';

  export default defineNuxtComponent({
    name: 'ArticleCardSmall',

    components: {
      AdaptivePictureArticleCardSmall,
      CardFooter,
      BHLink,
      SecretElems,
      TestPixel,
    },

    inject: {
      // Категория в которой происходит событие
      // Для аналитики
      eventCategory: {
        default() {
          return () => '';
        },
      },

      // Контекст события для аналитики
      // Пример: Переход из списка, из архива и т.д.
      eventActionArticle: {
        default() {
          return () => '';
        },
      },

      articleCardSmallType: {
        default() {
          return () => 'default';
        },
      },

      // Контекст события для аналитики
      eventName: {
        default() {
          return () => 'Переход в статью из малой акцентной карточки';
        },
      },

      // Контекст события для аналитики
      // Пример: Текст поиска по которой нашли статью
      eventItem: {
        default() {
          return () => '';
        },
      },

      // Контекст события для аналитики
      // Тип карточки: списка, архива, тега и т.д.
      eventElementListCard: {
        default() {
          return () => '';
        },
      },
    },

    props: {
      article: {
        type: Object as PropType<TArticle>,
        required: true,
      },

      meta: {
        type: Object as PropType<IMetaArchives | null>,
        default: null,
      },

      newTab: {
        type: Boolean,
        default: false,
      },

      imageLoading: {
        type: String as PropType<'lazy' | 'eager' | 'auto'>,
        default: undefined,
      },
    },

    data: () => ({
      coverWidth: [288],
      coverHeight: [192],
    }),

    computed: {
      ...mapState(useUserStore, ['isLoggedInWp']),

      type(): string {
        // @ts-expect-error injected functions are not typed in options API
        return this.articleCardSmallType();
      },

      articleCardSmallClass(): Record<string, boolean> {
        return {
          [`article-card-small--${this.type}`]: Boolean(this.type),
        };
      },

      target() {
        return this.newTab || this.article.teaser_target === '_blank' ? '_blank' : null;
      },

      isShowFooter(): boolean {
        return this.type === 'default';
      },

      date(): string {
        return this.getDateText(this.article.date);
      },

      coverImage(): TImageSize {
        return this.filterImages(this.article.image.sizes, this.coverWidth, this.coverHeight)[0];
      },
    },

    methods: {
      getDateText,
      removeBurninghutFromUrl,
      filterImages,

      sendAnalyticsArticleLink() {
        this.$emit('articleClick');
        // const currentSlug = getSlugFromUrl(this.$route.fullPath);
        // const nextPageSlug = getSlugFromUrl(this.article.url);
        // const eventName = this.eventName();
        // const eventItem = this.eventItem();
        // const eventElementListCard = this.eventElementListCard();
      },
    },
  });
</script>

<style lang="scss" scoped>
  .article-card-small {
    $article-card-small: &;

    padding: $spacing-5 14px;
    border-radius: 32px;
    position: relative;

    @include tablet {
      padding: $spacing-6;
      display: flex;
    }

    @include big-tablet {
      padding: 24px 32px;
    }

    @include desktop {
      padding: 24px 40px;
    }

    @include hover {
      &::before {
        position: absolute;
        content: '';
        left: 0;
        top: 0;
        border: 4px solid $hover-color--1;
        border-radius: 32px;
        height: 100%;
        width: 100%;
        z-index: 2;
      }

      .article-card-small {
        &__image {
          transform: scale(1.1);
        }

        &__title {
          @include gradientTextPseudoEl(
            $gradient: linear-gradient(90deg, #c722ca 17.52%, #0dbebc 78.21%)
          );
        }
      }
    }

    &__secret {
      margin-bottom: 8px;
      position: relative;
      z-index: 4;
    }

    &__link {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: 3;
      @include disable-link-drag;
    }

    &__image-wrap {
      border: 4px solid $border-color--1;
      max-width: 192px;
      width: 100%;
      height: 128px;
      border-radius: 32px;
      overflow: hidden;
      margin: 0 auto $spacing-5;
      transform: translateZ(0);

      @include tablet {
        margin: 0 $spacing-6 0 0;
        max-width: 240px;
        flex-shrink: 0;
        height: 160px;
      }
    }

    &__body {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    &__description {
      margin-bottom: $spacing-5;
      margin-top: $spacing-2;
      width: 100%;

      @include tablet {
        margin-bottom: $spacing-4;
      }
    }

    &__title {
      font-family: $headline-font--1;
      margin-bottom: $spacing-2;
      position: relative;
      word-break: break-word;
      font-weight: 700;
      -webkit-font-smoothing: antialiased;
      @include mediumText;

      @include tablet {
        @include bigText;
      }
    }

    &__description-text {
      word-break: break-word;
      font-family: $text-font--1;
      @include smallText;

      @include tablet {
        @include normalText;
      }
    }

    &__image {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.2s linear;
    }

    &.article-card-small--light {
      padding-left: 16px;
      padding-right: 16px;

      @include tablet {
        padding-left: 24px;
        padding-right: 24px;
      }

      @include big-tablet {
        padding-left: 32px;
        padding-right: 32px;
      }

      @include desktop {
        padding-left: 40px;
        padding-right: 40px;
      }

      .article-card-small {
        &__description {
          margin-top: 0px;
          margin-bottom: 0;
        }
      }
    }
  }
  :deep() {
    .card-footer {
      @include hover {
        color: $mainBlack;
      }
      &__icon {
        color: $icon-color--1;
        @include hover {
          color: $mainBlack;
        }
      }
      &__categories {
        &--category {
          @include hover {
            color: $mainBlack;
          }
        }
      }
    }
  }
</style>
